import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unwrap, messageOf } from '@truecare/api-client';
import { Alert, Button, Card, CardHeader } from '@truecare/ui';
import { api } from '../api';

export const HOMECARE_AGREEMENT_KEY = ['homecare', 'agreement'];

/**
 * Phase 23 (23-6) — whether the homecare client has accepted the current Client
 * Agreement version. Drives the booking gate (the Visits/Book tab is hidden
 * until accepted; the backend also 409s `client_agreement_not_accepted`).
 */
export function useHomecareAgreement() {
  const q = useQuery({
    queryKey: HOMECARE_AGREEMENT_KEY,
    queryFn: () => unwrap(api.GET('/api/v1/me/homecare/agreement')),
  });
  return {
    accepted: q.data?.accepted ?? false,
    isLoading: q.isLoading,
    data: q.data,
  };
}

/**
 * Client Agreement acceptance card. Clickwrap + typed legal name (POSTs
 * { version, typedLegalName }; a stale version 409s → reload). The operator-
 * authored body is rendered as HTML — safe because it is sanitized on ingest
 * (Phase 23 H2 allowlist), so no script/handler/external-resource survives.
 */
export function HomecareAgreementCard() {
  const qc = useQueryClient();
  const [agreed, setAgreed] = useState(false);
  const [typedName, setTypedName] = useState('');

  const stateQuery = useQuery({
    queryKey: HOMECARE_AGREEMENT_KEY,
    queryFn: () => unwrap(api.GET('/api/v1/me/homecare/agreement')),
  });

  const accept = useMutation({
    mutationFn: (body: { version: string; typedLegalName: string }) =>
      unwrap(api.POST('/api/v1/me/homecare/agreement', { body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: HOMECARE_AGREEMENT_KEY });
    },
  });

  const data = stateQuery.data;
  const version = data?.currentVersion;
  const canSubmit =
    agreed && typedName.trim().length >= 2 && !!version && !accept.isPending;

  return (
    <Card padding="lg" accent="left" tone="gold">
      <CardHeader
        title={data?.title ?? 'Client Agreement'}
        description="Please review and accept before booking your first visit."
      />
      {data ? (
        <div
          className="mt-4 max-h-64 overflow-y-auto rounded-md border border-border bg-paper p-4 font-body text-sm leading-relaxed text-ink [&_h1]:mb-2 [&_h1]:text-base [&_h1]:font-semibold [&_p]:mb-3"
          // Operator-authored agreement HTML — sanitized on ingest (Phase 23 H2).
          dangerouslySetInnerHTML={{ __html: data.bodyHtml }}
        />
      ) : (
        <p className="mt-4 font-body text-sm text-muted">Loading agreement…</p>
      )}

      {accept.isError && (
        <Alert tone="danger" title="Couldn't record your acceptance">
          {messageOf(accept.error, 'Please reload and try again.')}
        </Alert>
      )}

      <label className="mt-4 flex items-start gap-2 font-body text-sm">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-1"
          data-testid="homecare-agreement-checkbox"
        />
        <span>I have read and agree to this Client Agreement.</span>
      </label>

      <label className="mt-3 block font-body text-sm text-ink">
        Type your full legal name to sign
        <input
          type="text"
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          placeholder="Your full legal name"
          className="mt-1 w-full rounded-md border border-border bg-paper px-3 py-2 font-body text-sm text-ink"
          data-testid="homecare-agreement-typed-name"
        />
      </label>

      <div className="mt-4">
        <Button
          type="button"
          variant="primary"
          disabled={!canSubmit}
          onClick={() =>
            version &&
            accept.mutate({ version, typedLegalName: typedName.trim() })
          }
          data-testid="homecare-agreement-accept-button"
        >
          {accept.isPending ? 'Accepting…' : 'Accept agreement'}
        </Button>
      </div>
    </Card>
  );
}
